// src/a2a-log-gate.ts
/**
 * Change-gate for the `[listA2AChannels]` log line — LEAF MODULE (#798).
 *
 * That line was an unconditional `console.log` inside `listA2AChannels()`, which
 * polls every 30 seconds. Measured 2026-08-07/08:
 *
 *   bridge.log                90,841 of 100,702 lines (90.2%), 7.5 MB
 *   wren's gateway.log        27.9 MB, tail entirely this line
 *
 * Every one of them read `channels=0`. Real signal sat at a 10:1 noise ratio,
 * which is what pushed the 08-06 investigation onto `tail -N` — a sliding
 * window, not a clock.
 *
 * ⚠️ NOT deleted, deliberately. **#794 is an active watch on A2A behaviour**
 * (the loop guard went prompt-level when #792 removed the accidental rate
 * limiter), and this line is the only report of A2A channel state. Deleting it
 * would remove the signal that watch depends on. Logging only on CHANGE keeps
 * every transition and drops the ~2,880 identical lines a day.
 *
 * ⚠️ Deliberately does NOT timestamp. The OpenClaw gateway already stamps this
 * line on its own path (`2026-08-08T06:23:57.536-05:00 [listA2AChannels] …`), so
 * adding one here would double-stamp for every OpenClaw agent. On the bridge
 * path it stays unstamped — acceptable at a handful of lines a day, and the
 * bridge's OWN sinks are stamped by #796.
 */

/** The fields of a channel that make a state worth reporting. */
export interface A2AChannelLike {
  channelId?: string;
  status?: string;
  participant_count?: number;
}

/**
 * Stable fingerprint of the A2A channel set.
 *
 * **Sorted by id on purpose.** The server does not promise ordering, and an
 * order-sensitive signature would log a "change" every time the same channels
 * came back shuffled — reintroducing the noise this exists to remove, in a form
 * that looks like real activity.
 *
 * Only id / status / participant count are included: those are what the log line
 * reports and what #794 needs to see move.
 */
export function a2aChannelSignature(channels: readonly A2AChannelLike[]): string {
  return JSON.stringify(
    channels
      .map((c) => ({
        id: c.channelId?.slice(0, 8),
        status: c.status,
        participants: c.participant_count,
      }))
      .sort((a, b) => (a.id ?? "").localeCompare(b.id ?? "")),
  );
}

/**
 * Tracks the last reported signature so a caller can log only on change.
 *
 * First observation always reports: at process start the current state is news,
 * and a silent start would leave an operator unable to tell "no channels" from
 * "not running".
 */
export class A2ALogGate {
  private last: string | null = null;

  /** True when this state differs from the last reported one. Records it. */
  shouldReport(channels: readonly A2AChannelLike[]): boolean {
    const sig = a2aChannelSignature(channels);
    if (sig === this.last) return false;
    this.last = sig;
    return true;
  }
}
